import React from "react";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { TrimText } from "../common/TrimText";
import Header from "../common/Header";
import SideBar from "../common/SideBar";
import Program from "./program/Program.js";
import ProgramDetail from "./program/ProgramDetail";
import AuditTrail from "./program/AuditTrail";
import AssignedTable from "./program/ProgramDetailBlock/AssignedTable";
import FeaturesBenefits from "./program/FeaturesBenefits";
import ShortCourse from "./ShortCourses/ShortCourse.js";
import ShortCourseDetails from "./ShortCourses/ShortCourseDetails.js";
import ProgrammeDate from "./program/ProgrammeDate";
import CourseDates from "./ShortCourses/CourseDates.js";
import ShortCourseOutcomes from "./ShortCourses/ShortCourseOutcomes";
import ShortCourseTopics from "./ShortCourses/ShortCourseTopics";
import ShortCourseFeaturesBenefits from "./ShortCourses/ShortCourseFeaturesBenefits";
import StudyPhasesList from "./program/studyPaths/StudyPhasesList";
import RearrangePhases from "./program/studyPaths/RearrangePhases";
import PhaseForm from "./program/studyPaths/PhaseForm";
import GraduatesTable from "./program/GraduatesTable.js";
import PermissionsGate from "../../utils/permissionGate.js";
import SoftwareUsed from "./program/SoftwareUsed.js";
import Resources from "./program/Resources.js";
import ShortCourseResources from "./ShortCourses/ShortCourseResources.js";
import VersionsList from "./program/ProgramDetailBlock/VersionsList.js";
import VersionPageData from "./program/ProgramDetailBlock/VersionPageData.js";
import ProgrameGradeSetting from "../courses/Programs/ProgrameGradeSetting.js";
import PMCGradeBook from "./program/ProgramDetailBlock/PMCGradebook/PMCGradeBook.js";
import LinkedProgrammes from "./program/LinkedProgrammes.js";
import AdvancedTabs from "../library/AdvancedTabs";
import ConfirmationModal from "./program/linkedProgrammes/ConfirmationModal";
import SegmentedToggle from "../library/SegmentedToggle";
import SelectDropdown from "../library/SelectDropdown";
import usePrograms from "../../hooks/usePrograms";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import { getProgramsTabs } from "../../utils/ProgramsTabs";
import { GlobalIconNames } from "../../utils/GlobalIconNames";
import "./programsTable.css";
import {
  StyledBlockHeading,
  StyledHeader,
  StyledTitle,
  StyledPmcProgrammeSpan,
  StyledProgrammeNameSpan,
  StyledActions,
  StyledActionButton,
  StyledActionDivider,
  StyledWebsiteSelect,
  StyledControlsRow,
  StyledButtonGroup,
  StyledToolbarButton,
  StyledOutlineButton,
  StyledLockActionIcon,
  StyledUnlockActionIcon,
} from "./Programs.styles";

const VERSION_VIEW_SCOPES = ["pmcversionview"];

function Programs() {
  const {
    history,
    tab,
    type,
    id,
    programId,
    programName,
    detail,
    isDetailLoading,
    isProgrammeDetailTab,
    QAview,
    setQAview,
    setRefresh,
    versionData,
    vDataloading,
    setVDataLoading,
    versionDataError,
    gradeLockLoading,
    setGradeLockLoading,
    linkedQualifications,
    setLinkedQualifications,
    linkedQualificationsLoading,
    setLinkedQualificationsLoading,
    updateData,
    setUpdateData,
    linkProgrammeOpen,
    setLinkProgrammeOpen,
    topGradeConfirmation,
    setTopGradeConfirmation,
    websitePages,
    websitePagesLoading,
    queryVersionId,
    queryProgrammeId,
    queryType,
    runTopGradeAction,
    handleLockAllGrades,
    handleUnlockAllGrades,
  } = usePrograms();

  const isLockAction = topGradeConfirmation?.type === "lock";

  const isGradeLockActionBusy =
    gradeLockLoading.lockAll ||
    gradeLockLoading.unlockAll ||
    Object.keys(gradeLockLoading).some(
      (key) =>
        key !== "lockAll" &&
        key !== "unlockAll" &&
        gradeLockLoading[key]
    );

  const canViewVersions = usePermissionGuard({ scopes: VERSION_VIEW_SCOPES });

  // 1 = study-path-linked re-registration (Programme Integration) is enabled
  const isIntegrationEnabled = Number(detail?.EnableStudypathLinkReReg) === 1;

  const tabs = getProgramsTabs({
    history,
    tab,
    type,
    id,
    detail,
    queryVersionId,
    queryProgrammeId,
    canViewVersions,
  });

  const isListView =
    type === "table" &&
    (tab === "programme" ||
      tab === "shortCourse" ||
      tab === "auditTrail");

  const hasStickyFooter =
    tab === "programme" &&
    (type === "open" || type === "add" || type === "duplicate");

  /*
   * The Linked Programmes table already reserves exact scroll room for its
   * floating bulk-action bar via its own dynamic spacer (LinkedProgrammes.styles.js'
   * StyledBulkActionSpacer, sized to the bar's measured height). What that spacer
   * doesn't control is .content-wrapper's scrollbar itself: selecting rows grows
   * the page just enough to flip the container from "fits, no scrollbar" to
   * "scrolls", which shifts all the content sideways by the scrollbar's width —
   * the "checking causes layout shift" bug. Reserving the scrollbar's gutter
   * permanently on this tab keeps that width stable whether or not anything's
   * selected, and keeps the track visible so it's obvious more content can be
   * scrolled to once the bar appears.
   */
  const hasFloatingBar =
    tab === "linked_programmes" && type === "table";

  const isVersionDetailContext =
    tab === "versions" &&
    type !== "table" &&
    type !== "create";

  const isVersionsListContext =
    tab === "versions" && type === "table";

  const isVersionLinkedProgrammes =
    tab === "versions" &&
    type === "linked_programmes" &&
    queryType === "table";

  const isVersionGraduates =
    tab === "versions" &&
    type === "graduates" &&
    queryType === "table";

  /*
   * On the Versions screens specifically, Active Gradebook/Website Page must stay
   * present (disabled, with a reason) rather than vanish while `detail` is still
   * loading - see usePrograms.js's isDetailLoading. Elsewhere in the PMC shell the
   * pre-existing hide-until-loaded behaviour is unchanged, out of scope here.
   */
  const isVersionsRoute = tab === "versions";
  /*
   * type !== "duplicate" stops this button reappearing right after a programme copy
   * (regressed in 594b293655, which dropped that exclusion while rewriting this gate) -
   * restored here, applied only outside the Versions route since "duplicate" isn't a
   * Versions `type` value.
   */
  const hasActiveVersion = isIntegrationEnabled && Boolean(detail?.version_id);
  const showActiveGradebookAction =
    isProgrammeDetailTab &&
    (isVersionsRoute
      ? type === "table" && (isDetailLoading || hasActiveVersion)
      : type !== "duplicate" && hasActiveVersion);
  const isProgrammeDetailKnown = Boolean(detail?.id || detail?.Id);
  const showWebsitePageAction =
    isProgrammeDetailTab &&
    (isVersionsRoute
      ? isDetailLoading || isProgrammeDetailKnown
      : isProgrammeDetailKnown);
  const isWebsitePageLoading = isDetailLoading || websitePagesLoading;

  return (
    <>
      <Header />

      <div className={`content-wrapper d-flex flex-wrap new-standard-box${hasFloatingBar ? " pmc-linked-programmes-scroll" : ""}`}>
        <SideBar />

        <div className={`sidebar-right${hasStickyFooter ? " bottom-100" : ""}`}>
          <div className="sub-menu-content-block">
            <div className="sub-menu-right-block">
              <div className="wrap-box-right">
                <div className={isListView ? "my-tickets-heading-blk flex-direction mb-2" : "my-tickets-heading-blk flex-direction mb-3 pmc-page-heading"}>
                  {isListView ? (
                    <div className="d-flex align-items-center ">
                      <div className="name-w-head d-flex align-items-center ">
                        <h4 className="text-left-align landing-heading heading_color_dynamic">
                          Programmes & <span>Courses</span>
                        </h4>
                      </div>

                      <div className="text-md-right action2-blk">
                        <div className="ticket-view-btn d-flex align-items-center">
                          <div className="add-new-ticket-bx">
                            <StyledActionButton onClick={() => history.goBack()} title="Back">
                              <i className="fal fa-chevron-left" aria-hidden="true" />
                              Back
                            </StyledActionButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                  <StyledBlockHeading>
                    <StyledHeader>
                      <StyledTitle>
                        {isVersionDetailContext ? (
                          <>
                            <StyledPmcProgrammeSpan>
                              {TrimText(versionData?.pmc_name, 60)}
                              {versionData?.name && ":"}
                            </StyledPmcProgrammeSpan>

                            {versionData?.name && (
                              <StyledProgrammeNameSpan>
                                {versionData.name}
                              </StyledProgrammeNameSpan>
                            )}
                          </>
                        ) : isVersionsListContext ? (
                          <>
                            <StyledPmcProgrammeSpan>
                              Programme:
                            </StyledPmcProgrammeSpan>

                            <StyledProgrammeNameSpan>
                              {TrimText(programName, 60)}
                            </StyledProgrammeNameSpan>
                          </>
                        ) : (
                          <>
                            <StyledPmcProgrammeSpan>
                              PMC Programme:
                            </StyledPmcProgrammeSpan>

                            <StyledProgrammeNameSpan>
                              {TrimText(programName, 60)}
                            </StyledProgrammeNameSpan>
                          </>
                        )}
                      </StyledTitle>

                      <StyledActions>
                        <StyledActionDivider
                          aria-hidden="true"
                        />

                        <StyledActionButton
                          onClick={() =>
                            history.goBack()
                          }
                          title="Back"
                        >
                          <i
                            className="fal fa-chevron-left"
                            aria-hidden="true"
                          />

                          Back
                        </StyledActionButton>

                        {showActiveGradebookAction && (
                            <>
                              <StyledActionDivider
                                aria-hidden="true"
                              />

                              <StyledActionButton
                                onClick={() => {
                                  if (!isDetailLoading) {
                                    history.push(
                                      `/program-management-center/programmes/versions/gradebook?versionId=${
                                        detail?.version_id ??
                                        queryVersionId
                                      }&programmeId=${
                                        queryProgrammeId ??
                                        id
                                      }`
                                    );
                                  }
                                }}
                                disabled={isDetailLoading}
                                title={
                                  isDetailLoading
                                    ? "Loading…"
                                    : "Go to current active versions gradebook"
                                }
                              >
                                <i
                                  className={`fal fa-${isDetailLoading ? "spinner fa-spin" : "table-pivot"}`}
                                  aria-hidden="true"
                                />

                                Active Gradebook
                              </StyledActionButton>
                            </>
                          )}

                        {showWebsitePageAction &&
                          websitePages.length <=
                            1 && (
                            <>
                              <StyledActionDivider aria-hidden="true" />

                              <StyledActionButton
                                type="button"
                                onClick={() => {
                                  if (websitePages[0] && websitePages[0].url) {
                                    window.open(websitePages[0].url, "_blank", "noopener,noreferrer");
                                  }
                                }}
                                disabled={isWebsitePageLoading || websitePages.length === 0}
                                title={
                                  isWebsitePageLoading
                                    ? "Loading…"
                                    : websitePages.length === 0
                                      ? "No Website Page Linked"
                                      : websitePages[0]?.label || "Website Page"
                                }
                              >
                                <i
                                  className={`fal fa-${
                                    isWebsitePageLoading ? "spinner fa-spin" : "globe"
                                  }`}
                                  aria-hidden="true"
                                />

                                Website Page
                              </StyledActionButton>
                            </>
                          )}

                        {showWebsitePageAction &&
                          websitePages.length >
                            1 && (
                            <>
                              <StyledActionDivider
                                aria-hidden="true"
                              />

                            <StyledWebsiteSelect>
                              <SelectDropdown
                                size="sm"
                                variant="filter"
                                icon={websitePagesLoading ? "spinner fa-spin" : "globe"}
                                placeholder="Website Page"
                                options={websitePages.map((page) => ({
                                  value: page.url,
                                  label: page.label,
                                }))}
                                onChange={(event) => {
                                  const url =
                                    event && event.target ? event.target.value : "";

                                  if (url) {
                                    window.open(url, "_blank", "noopener,noreferrer");
                                  }
                                }}
                                loading={websitePagesLoading}
                                emptyText="No Website Page Linked"
                              />
                            </StyledWebsiteSelect>
                          </>
                        )}
                      </StyledActions>
                    </StyledHeader>
                  </StyledBlockHeading>
                  )}
                </div>

                <div className="tabs-wrap">
                  {type !== "add" &&
                    type !== "duplicate" &&
                    type !== "clone" && (
                      isListView ? (
                        <ul className="nav nav-pills mb-4" id="tabs" role="tablist">
                          <li
                            className="nav-item"
                            onClick={() => history.push("/program-management-center/programmes/programme/table")}
                          >
                            <Link
                              className={`nav-link ${tab === "programme" ? "active" : ""}`}
                              id="pills-tab1"
                              data-toggle="pill"
                              to="/program-management-center/programmes/programme/table"
                              href="#pills-tab11"
                              role="tab"
                              aria-controls="pills-tab11"
                              aria-selected="true"
                            >
                              {GlobalIconNames("Programmes").html}
                            </Link>
                          </li>

                          <li
                            className="nav-item"
                            onClick={() => history.push("/program-management-center/programmes/shortCourse/table")}
                          >
                            <Link
                              className={`nav-link ${tab === "shortCourse" ? "active" : ""}`}
                              id="pills-tab1"
                              data-toggle="pill"
                              to="/program-management-center/programmes/shortCourse/table"
                              href="#pills-tab11"
                              role="tab"
                              aria-controls="pills-tab11"
                              aria-selected="true"
                            >
                              {GlobalIconNames("shortcourse").html}
                            </Link>
                          </li>

                          <li
                            className="nav-item"
                            onClick={() => history.push("/program-management-center/programmes/auditTrail/table")}
                          >
                            <Link
                              className={`nav-link ${tab === "auditTrail" ? "active" : ""}`}
                              id="pills-tab2"
                              data-toggle="pill"
                              to="/program-management-center/programmes/auditTrail/table"
                              href="#pills-tab12"
                              role="tab"
                              aria-controls="pills-tab12"
                              aria-selected="true"
                            >
                              {GlobalIconNames("audittrail").html}
                            </Link>
                          </li>
                        </ul>
                      ) : (
                        <AdvancedTabs
                          tabs={tabs}
                        />
                      )
                    )}

                  <div
                    className="tab-content"
                    id="pills-tabContent"
                  >
                    <div
                      className="tab-pane fade active show"
                      id="pills-tab11"
                      role="tabpanel"
                      aria-labelledby="pills-tab1"
                    >
                      {tab ===
                        "programme" &&
                        type ===
                          "table" && (
                          <Program />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab14"
                      role="tabpanel"
                      aria-labelledby="pills-tab4"
                    >
                      {tab ===
                        "features-benefits" &&
                        (type ===
                          "table" ||
                          type ===
                            "re-arrange") && (
                          <FeaturesBenefits />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab13"
                      role="tabpanel"
                      aria-labelledby="pills-tab3"
                    >
                      {tab ===
                        "versions" &&
                        type ===
                          "assigned-modules" &&
                        queryType ===
                          "table" && (
                          <AssignedTable
                            id={
                              programId
                            }
                            versionData={
                              versionData
                            }
                          />
                        )}
                    </div>

                    {tab ===
                      "versions" &&
                      type ===
                        "table" && (
                        <div
                          className="tab-pane fade active show"
                          id="pills-tab13"
                          role="tabpanel"
                          aria-labelledby="pills-tab3"
                        >
                          <VersionsList
                            id={
                              programId
                            }
                          />
                        </div>
                      )}

                    {tab ===
                      "versions" &&
                      (type ===
                        "create" ||
                        type ===
                          "details" ||
                        type ===
                          "clone") && (
                        <div
                          className="tab-pane fade active show"
                          id="pills-tab130"
                          role="tabpanel"
                          aria-labelledby="pills-tab130"
                        >
                          <VersionPageData
                            setRefresh={
                              setRefresh
                            }
                            loading={
                              vDataloading
                            }
                            setLoading={
                              setVDataLoading
                            }
                            versionData={
                              versionData
                            }
                            versionDataError={
                              versionDataError
                            }
                            onRetryVersionData={() =>
                              setRefresh(Date.now())
                            }
                          />
                        </div>
                      )}

                    {tab ===
                      "versions" &&
                      type ===
                        "study_path" &&
                      queryType ===
                        "table" && (
                        <div
                          className="tab-pane fade active show"
                          id="pills-tab13"
                          role="tabpanel"
                          aria-labelledby="pills-tab3"
                        >
                          <StudyPhasesList
                            id={
                              programId
                            }
                            versionData={
                              versionData
                            }
                          />
                        </div>
                      )}

                    {tab ===
                      "versions" &&
                      type ===
                        "gradeSetting" && (
                        <div
                          className="tab-pane fade active show"
                          id="pills-tab19"
                          role="tabpanel"
                          aria-labelledby="pills-tab9"
                        >
                          <ProgrameGradeSetting
                            versionData={
                              versionData
                            }
                          />
                        </div>
                      )}

                    {tab ===
                      "versions" &&
                      type ===
                        "gradebook" && (
                        <div
                          className="tab-pane fade active show"
                          id="pills-tab19"
                          role="tabpanel"
                          aria-labelledby="pills-tab9"
                        >
                          <PMCGradeBook
                            versionData={
                              versionData
                            }
                            isActiveVersion={
                              String(detail?.version_id) ===
                              String(versionData?.id)
                            }
                          />
                        </div>
                      )}

                    {tab ===
                      "versions" &&
                      type ===
                        "study_path" &&
                      queryType ===
                        "re-arrange" && (
                        <div
                          className="tab-pane fade active show"
                          id="pills-tab13"
                          role="tabpanel"
                          aria-labelledby="pills-tab3"
                        >
                          <RearrangePhases
                            id={
                              programId
                            }
                            versionData={
                              versionData
                            }
                          />
                        </div>
                      )}

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab13"
                      role="tabpanel"
                      aria-labelledby="pills-tab3"
                    >
                      {((tab ===
                        "linked_programmes" &&
                        type ===
                          "table") ||
                        isVersionLinkedProgrammes) && (
                          <>
                            <StyledControlsRow>
                              {isIntegrationEnabled && (
                                <SegmentedToggle
                                  ariaLabel="Programme view"
                                  value={QAview ? "qa" : "standard"}
                                  onChange={(value) => setQAview(value === "qa")}
                                  options={[
                                    { value: "standard", label: "Standard View" },
                                    { value: "qa", label: "QA View" },
                                  ]}
                                />
                              )}

                              <StyledButtonGroup>
                                {linkedQualificationsLoading ? (
                                  <>
                                    <Skeleton
                                      width={142}
                                      height={35}
                                      borderRadius={10}
                                    />
                                    <Skeleton
                                      width={158}
                                      height={35}
                                      borderRadius={10}
                                    />
                                  </>
                                ) : (
                                  linkedQualifications?.length >
                                    0 && (
                                    <>
                                      <PermissionsGate
                                        scopes={[
                                          "capgsedit",
                                        ]}
                                        errorProps={{
                                          disabled:
                                            true,
                                        }}
                                      >
                                        <StyledOutlineButton
                                          onClick={
                                            handleLockAllGrades
                                          }
                                          disabled={
                                            isGradeLockActionBusy
                                          }
                                        >
                                          {gradeLockLoading.lockAll ? (
                                            <StyledLockActionIcon
                                              className="fal fa-sync fa-spin"
                                              aria-hidden="true"
                                            />
                                          ) : (
                                            <StyledLockActionIcon
                                              className="fas fa-lock"
                                              aria-hidden="true"
                                            />
                                          )}

                                          Lock
                                          All
                                          Grades
                                        </StyledOutlineButton>
                                      </PermissionsGate>

                                      <PermissionsGate
                                        scopes={[
                                          "capgsedit",
                                        ]}
                                        errorProps={{
                                          disabled:
                                            true,
                                        }}
                                      >
                                        <StyledOutlineButton
                                          onClick={
                                            handleUnlockAllGrades
                                          }
                                          disabled={
                                            isGradeLockActionBusy
                                          }
                                        >
                                          {gradeLockLoading.unlockAll ? (
                                            <StyledUnlockActionIcon
                                              className="fal fa-sync fa-spin"
                                              aria-hidden="true"
                                            />
                                          ) : (
                                            <StyledUnlockActionIcon
                                              className="fas fa-unlock"
                                              aria-hidden="true"
                                            />
                                          )}

                                          Unlock
                                          All
                                          Grades
                                        </StyledOutlineButton>
                                      </PermissionsGate>
                                    </>
                                  )
                                )}

                                <StyledToolbarButton
                                  type="button"
                                  $tone="primary"
                                  onClick={() =>
                                    setLinkProgrammeOpen(
                                      true
                                    )
                                  }
                                >
                                  <i
                                    className="fal fa-link"
                                    aria-hidden="true"
                                  />

                                  Link
                                  Programme
                                </StyledToolbarButton>
                              </StyledButtonGroup>
                            </StyledControlsRow>

                            <LinkedProgrammes
                              detail={
                                detail
                              }
                              QAview={
                                QAview
                              }
                              id={
                                programId
                              }
                              versionId={
                                isVersionLinkedProgrammes
                                  ? queryVersionId
                                  : undefined
                              }
                              isVersionEnabled={
                                isIntegrationEnabled
                              }
                              gradeLockLoading={
                                gradeLockLoading
                              }
                              setGradeLockLoading={
                                setGradeLockLoading
                              }
                              linkedQualifications={
                                linkedQualifications
                              }
                              setLinkedQualifications={
                                setLinkedQualifications
                              }
                              setLinkedQualificationsLoading={
                                setLinkedQualificationsLoading
                              }
                              updateData={
                                updateData
                              }
                              setUpdateData={
                                setUpdateData
                              }
                              linkProgrammeOpen={
                                linkProgrammeOpen
                              }
                              onCloseLinkProgramme={() =>
                                setLinkProgrammeOpen(
                                  false
                                )
                              }
                            />
                          </>
                        )}

                      <ConfirmationModal
                        open={!!topGradeConfirmation}
                        width={isLockAction ? 506 : 546}
                        copy={
                          topGradeConfirmation && {
                            title: isLockAction
                              ? `Lock grades on all ${
                                  topGradeConfirmation.count
                                } linked programme${
                                  topGradeConfirmation.count === 1
                                    ? ""
                                    : "s"
                                }?`
                              : `Unlock grades on all ${
                                  topGradeConfirmation.count
                                } linked programme${
                                  topGradeConfirmation.count === 1
                                    ? ""
                                    : "s"
                                }?`,
                            description: isLockAction
                              ? "All grades will become read-only for all modules and assessments linked to these programmes."
                              : "Grades will become editable again for all modules and assessments linked to these programmes.",
                            label: isLockAction
                              ? "Lock Grades"
                              : "Unlock Grades",
                            icon: isLockAction ? "lock" : "unlock",
                            tone: "primary",
                          }
                        }
                        onClose={() =>
                          setTopGradeConfirmation(
                            null
                          )
                        }
                        onConfirm={() =>
                          runTopGradeAction(isLockAction ? 1 : 0) // 1 = lock, 0 = unlock
                        }
                      />
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab8"
                      role="tabpanel"
                      aria-labelledby="pills-tab8"
                    >
                      {tab ===
                        "softwareused" &&
                        type ===
                          "table" && (
                          <SoftwareUsed />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab13"
                      role="tabpanel"
                      aria-labelledby="pills-tab3"
                    >
                      {tab ===
                        "resources" &&
                        type ===
                          "table" && (
                          <Resources />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab13"
                      role="tabpanel"
                      aria-labelledby="pills-tab3"
                    >
                      {tab ===
                        "shortCourse-resources" &&
                        type ===
                          "table" && (
                          <ShortCourseResources />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab13"
                      role="tabpanel"
                      aria-labelledby="pills-tab3"
                    >
                      {((tab ===
                        "graduates" &&
                        type ===
                          "table") ||
                        isVersionGraduates) && (
                          <>
                            <GraduatesTable
                              detail={
                                detail
                              }
                              id={
                                programId
                              }
                            />
                          </>
                        )}
                    </div>

                    {tab ===
                      "versions" &&
                      type ===
                        "study_path" &&
                      (queryType ===
                        "create" ||
                        queryType ===
                          "open") && (
                        <div
                          className="tab-pane fade active show"
                          id="pills-tab13"
                          role="tabpanel"
                          aria-labelledby="pills-tab3"
                        >
                          <PhaseForm
                            detail={
                              detail
                            }
                            versionData={
                              versionData
                            }
                            id={
                              programId
                            }
                          />
                        </div>
                      )}

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab12"
                      role="tabpanel"
                      aria-labelledby="pills-tab2"
                    >
                      {tab ===
                        "programme" &&
                        (type ===
                          "open" ||
                          type ===
                            "add" ||
                          type ===
                            "duplicate") && (
                          <ProgramDetail
                            detail={
                              detail
                            }
                            setRefresh={
                              setRefresh
                            }
                            setQAview={
                              setQAview
                            }
                            versionData={
                              versionData
                            }
                          />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab113"
                      role="tabpanel"
                      aria-labelledby="pills-tab1"
                    >
                      {tab ===
                        "shortCourse" &&
                        type ===
                          "table" && (
                          <ShortCourse />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab122"
                      role="tabpanel"
                      aria-labelledby="pills-tab2"
                    >
                      {tab ===
                        "shortCourse" &&
                        (type ===
                          "open" ||
                          type ===
                            "add" ||
                          type ===
                            "duplicate") && (
                          <ShortCourseDetails
                            detail={
                              detail
                            }
                          />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab922"
                      role="tabpanel"
                      aria-labelledby="pills-tab2"
                    >
                      {tab ===
                        "programmeDates" &&
                        type ===
                          "table" && (
                          <ProgrammeDate />
                        )}

                      {tab ===
                        "courseDates" &&
                        type ===
                          "table" && (
                          <CourseDates />
                        )}
                    </div>

                    {(tab ===
                      "auditTrail" ||
                      tab ===
                        "shortCourseAuditTrail" ||
                      (tab ===
                        "versions" &&
                        type ===
                          "auditTrail")) && (
                      <div
                        className="tab-pane fade active show"
                        id="pills-tab12"
                        role="tabpanel"
                        aria-labelledby="pills-tab2"
                      >
                        <AuditTrail />
                      </div>
                    )}

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab14"
                      role="tabpanel"
                      aria-labelledby="pills-tab4"
                    >
                      {tab ===
                        "shortCourseFeatures-benefits" &&
                        (type ===
                          "table" ||
                          type ===
                            "re-arrange") && (
                          <ShortCourseFeaturesBenefits />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab13"
                      role="tabpanel"
                      aria-labelledby="pills-tab3"
                    >
                      {tab ===
                        "shortCoursetopics" &&
                        type ===
                          "table" && (
                          <ShortCourseTopics />
                        )}
                    </div>

                    <div
                      className="tab-pane fade active show"
                      id="pills-tab14"
                      role="tabpanel"
                      aria-labelledby="pills-tab4"
                    >
                      {tab ===
                        "shortCourseOutcomes" &&
                        type ===
                          "table" && (
                          <ShortCourseOutcomes />
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Programs;
